import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import useSWR from "swr";

import { Icon } from "../components/Icon";
import {
  AdAvatar,
  Callout,
  ChipInput,
  Field,
  KeyValue,
  Pill,
  ToggleRow,
  errText,
} from "../admin/ui";
import {
  api,
  type AdminRole,
  type AdminTicketSyncOptions,
  type ApproverDesignation,
  type AutotaskCompanyDetail,
  type AutotaskCompanySearchItem,
  type FormAdmin,
  type SsoTrustMode,
} from "../services/api";

/* ===================================================================
   New-customer wizard — /admin/clients/new?step=n

   Six steps; only the first is required. Everything skipped is left
   visibly unfinished on the customer page rather than silently defaulted,
   which is the whole point of the setup-health dots.

   All state is in memory: a refresh restarts at step 0. Nothing is written
   until "Create customer", and that write is a short sequence (client →
   SSO policy → people → form availability → routing rule → ticket sync)
   whose partial failures are reported rather than rolled back — the
   customer exists by then, and the honest answer is "created, but these
   bits didn't apply".

   Ticket sync is the one step whose toggles all start ON, unlike the
   per-company column defaults (write_enabled / client_can_add_notes are
   false in the DB). That is deliberate: a customer being onboarded today
   gets the full ticket experience unless somebody switches a piece off
   here. Status changes follow write_enabled; there is no separate switch.
   The PUT still coerces write_enabled off unless the global master switch
   is on, and the wizard reports that back rather than pretending.
   =================================================================== */

type StepKey = "company" | "signin" | "people" | "forms" | "sync" | "review";

const STEPS: { key: StepKey; title: string; sub: string; desc: string }[] = [
  {
    key: "company",
    title: "Autotask company",
    sub: "Who are we onboarding",
    desc: "Pick the company from Autotask. Name, phone and address are copied across so you don't retype anything.",
  },
  {
    key: "signin",
    title: "Sign-in",
    sub: "Microsoft tenant & domains",
    desc: "Connect their Microsoft 365 tenant so staff can sign in with their work account.",
  },
  {
    key: "people",
    title: "People",
    sub: "First admin & contacts",
    desc: "Choose the first client admin. They'll manage their own people from there.",
  },
  {
    key: "forms",
    title: "Forms & approvals",
    sub: "What they can request",
    desc: "Start with the standard set. You can fine-tune per form once the customer exists.",
  },
  {
    key: "sync",
    title: "Ticket sync",
    sub: "Tickets, replies & status",
    desc: "How their Autotask tickets appear in the portal, and what portal users can do with them. Everything starts on.",
  },
  {
    key: "review",
    title: "Review & create",
    sub: "One click to finish",
    desc: "Everything below is editable later from the customer page.",
  },
];

type ApproverChoice = "manager" | "client_admin" | "service_desk";

const APPROVER_OPTIONS: { value: ApproverChoice; label: string }[] = [
  { value: "manager", label: "Requester's manager" },
  { value: "client_admin", label: "Client admin" },
  { value: "service_desk", label: "iQuest service desk" },
];

function approverDesignation(choice: ApproverChoice): ApproverDesignation {
  if (choice === "manager") return { designation: "direct_manager", value: null };
  return { designation: "role", value: choice };
}

/** "jane.smith@acme.com.au" -> "Jane Smith". */
function nameFromEmail(email: string): string {
  return (
    email
      .split("@")[0]
      .replace(/[._-]+/g, " ")
      .replace(/\b\w/g, (m) => m.toUpperCase())
      .trim() || email
  );
}

/** Bare host from a website field, so a picked company seeds its own domain. */
function domainFromWeb(web: string | null | undefined): string | null {
  if (!web) return null;
  const host = web
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .split("/")[0];
  return host.includes(".") ? host : null;
}

export function AdminClientNew() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const stepIndex = Math.min(
    Math.max(parseInt(params.get("step") ?? "0", 10) || 0, 0),
    STEPS.length - 1,
  );
  const step = STEPS[stepIndex];

  // ---- wizard state ----
  const [company, setCompany] = useState<AutotaskCompanyDetail | null>(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [stateVal, setStateVal] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [webAddress, setWebAddress] = useState("");

  const [tenantId, setTenantId] = useState("");
  const [trustMode, setTrustMode] = useState<SsoTrustMode>("b2b_guest");
  const [domains, setDomains] = useState<string[]>([]);
  const [autoProvision, setAutoProvision] = useState(true);
  const [requireMfa, setRequireMfa] = useState(true);

  const [selectedContacts, setSelectedContacts] = useState<number[]>([]);
  const [adminContactId, setAdminContactId] = useState<number | null>(null);
  const [adminEmail, setAdminEmail] = useState("");

  const [disabledForms, setDisabledForms] = useState<string[]>([]);
  const [approver, setApprover] = useState<ApproverChoice>("manager");

  // Ticket sync. All four toggles start ON (see the header note); the two
  // selects start unset and fall back to the instance-wide suggestion the
  // options endpoint returns, so "leave it alone" still stores a real queue
  // and the customer's sync health dot goes green instead of amber.
  const [readEnabled, setReadEnabled] = useState(true);
  const [writeEnabled, setWriteEnabled] = useState(true);
  const [syncNotes, setSyncNotes] = useState(true);
  const [clientCanAddNotes, setClientCanAddNotes] = useState(true);
  const [queueId, setQueueId] = useState<string | null>(null);
  const [priorityId, setPriorityId] = useState<string | null>(null);

  const [skipped, setSkipped] = useState<Partial<Record<StepKey, boolean>>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: forms } = useSWR<FormAdmin[]>(
    ["/api/admin/forms", "wizard"],
    () => api.admin.forms.list({ include_unpublished: false }),
  );
  // A brand-new customer sees the global catalogue; forms scoped to somebody
  // else are not theirs to switch on here.
  const globalForms = useMemo(
    () => (forms ?? []).filter((f) => f.scope === "global"),
    [forms],
  );

  // Field-global: the picklists carry no client data, so they load without a
  // company. A failure here is not fatal — the toggles still work, the selects
  // just come up empty.
  const { data: syncOptions } = useSWR<AdminTicketSyncOptions>(
    ["/api/admin/ticket-sync-options"],
    () => api.admin.clients.ticketSync.options(),
  );

  // null = untouched, so the suggestion applies; "" = deliberately cleared.
  const effectiveQueueId =
    queueId ??
    (syncOptions?.suggested_queue_id != null
      ? String(syncOptions.suggested_queue_id)
      : "");
  const effectivePriorityId =
    priorityId ??
    (syncOptions?.suggested_priority_id != null
      ? String(syncOptions.suggested_priority_id)
      : "");
  const writeGloballyOff = syncOptions ? !syncOptions.write_globally_enabled : false;

  function goToStep(next: number) {
    setParams({ step: String(next) }, { replace: false });
    window.scrollTo(0, 0);
  }

  function advance(markSkipped: boolean) {
    setSkipped((s) => ({ ...s, [step.key]: markSkipped }));
    goToStep(stepIndex + 1);
  }

  function pickCompany(detail: AutotaskCompanyDetail) {
    setCompany(detail);
    setName(detail.name ?? "");
    setPhone(detail.phone ?? "");
    setAddress1(detail.address1 ?? "");
    setAddress2(detail.address2 ?? "");
    setCity(detail.city ?? "");
    setStateVal(detail.state ?? "");
    setPostalCode(detail.postal_code ?? "");
    setWebAddress(detail.web_address ?? "");
    const seed = domainFromWeb(detail.web_address);
    setDomains(seed ? [seed] : []);
    setSelectedContacts([]);
    setAdminContactId(null);
  }

  const contacts = useMemo(
    () => (company?.contacts ?? []).filter((c) => c.email),
    [company],
  );

  async function handleCreate() {
    if (!company) return;
    setBusy(true);
    setError(null);
    const problems: string[] = [];
    try {
      const created = await api.admin.clients.create({
        name: name.trim(),
        autotask_company_id: company.autotask_id,
        m365_tenant_id: tenantId.trim() || null,
        sso_trust_mode: trustMode,
        phone: phone.trim() || null,
        address1: address1.trim() || null,
        address2: address2.trim() || null,
        city: city.trim() || null,
        state: stateVal.trim() || null,
        postal_code: postalCode.trim() || null,
        web_address: webAddress.trim() || null,
      });

      // SSO policy is not part of the create payload, so it rides a follow-up
      // PATCH. Skipped step 2 means nothing to send.
      if (!skipped.signin && (domains.length > 0 || tenantId.trim())) {
        try {
          await api.admin.clients.update(created.id, {
            allowed_email_domains: domains,
            auto_provision_users: autoProvision,
            require_mfa: requireMfa,
          });
        } catch (e) {
          problems.push(`Sign-in policy: ${errText(e, "could not be saved")}`);
        }
      }

      if (!skipped.people) {
        for (const contact of contacts) {
          if (!selectedContacts.includes(contact.id)) continue;
          const role: AdminRole =
            adminContactId === contact.id ? "client_admin" : "end_user";
          try {
            await api.admin.users.create({
              email: contact.email as string,
              display_name: contact.full_name || nameFromEmail(contact.email as string),
              role,
              company_id: created.id,
            });
          } catch (e) {
            problems.push(`${contact.full_name ?? contact.email}: ${errText(e, "not invited")}`);
          }
        }
        const fallbackEmail = adminEmail.trim();
        if (adminContactId === null && fallbackEmail) {
          try {
            await api.admin.users.create({
              email: fallbackEmail,
              display_name: nameFromEmail(fallbackEmail),
              role: "client_admin",
              company_id: created.id,
            });
          } catch (e) {
            problems.push(`${fallbackEmail}: ${errText(e, "not invited")}`);
          }
        }
      }

      if (!skipped.forms) {
        // Forms default to enabled, so only the unticked ones need a row.
        for (const formId of disabledForms) {
          try {
            await api.admin.clients.setFormConfig(created.id, formId, { enabled: false });
          } catch (e) {
            problems.push(`Form availability: ${errText(e, "not applied")}`);
          }
        }
        try {
          await api.admin.matrix.createRule(created.id, {
            priority: 100,
            name: "Any request with approval",
            condition_jsonb: {},
            action: "chain",
            chain_steps_jsonb: [
              {
                name: "Approval",
                type: "sequential",
                approvers: [approverDesignation(approver)],
              },
            ],
            form_scope_jsonb: null,
            is_active: true,
          });
        } catch (e) {
          problems.push(`Approval routing: ${errText(e, "no rule created")}`);
        }
      }

      // Ticket sync is applied unconditionally — there is no skip for it. The
      // wizard's toggle defaults are NOT the column defaults, so omitting this
      // PUT would silently hand the customer write-off / replies-off.
      try {
        const applied = await api.admin.clients.ticketSync.update(created.id, {
          read_enabled: readEnabled,
          write_enabled: writeEnabled,
          sync_notes: syncNotes,
          client_can_add_notes: clientCanAddNotes,
          default_queue_id: effectiveQueueId ? Number(effectiveQueueId) : null,
          default_priority_id: effectivePriorityId ? Number(effectivePriorityId) : null,
        });
        // The global master switch has the last word on write-back. Report it
        // rather than leaving the admin thinking they turned it on.
        if (applied.forced_off.includes("write_enabled")) {
          problems.push(
            "Ticket sync: write-back stays off until the global Autotask flag " +
              "is on. The rest of the ticket settings were saved.",
          );
        }
      } catch (e) {
        problems.push(`Ticket sync: ${errText(e, "settings not applied")}`);
      }

      navigate(`/admin/clients/${created.id}?new=1`, {
        replace: true,
        state: problems.length ? { setupProblems: problems } : undefined,
      });
    } catch (e) {
      setError(errText(e, "Could not create the customer."));
      setBusy(false);
    }
  }

  const canContinue = step.key !== "company" || Boolean(company && name.trim());

  return (
    <div className="rf-wrap">
      <Link className="ad-back" to="/admin/clients">
        <Icon name="arrowLeft" /> Customers
      </Link>
      <div className="ad-head" style={{ marginBottom: 20 }}>
        <div>
          <div className="ad-eyebrow">New customer</div>
          <h1 className="rf-h1">{name.trim() || "Onboard a customer"}</h1>
          <p className="rf-sub">
            Six short steps. Only the first is required — skip the rest and finish from
            the customer page later.
          </p>
        </div>
      </div>

      <div className="ad-wiz">
        <div className="ad-wiz-steps">
          {STEPS.map((s, i) => {
            const done = i < stepIndex;
            const wasSkipped = Boolean(skipped[s.key]);
            const reachable = i < stepIndex || (i > stepIndex && Boolean(company));
            const cls = [
              "ad-wstep",
              done ? "done" : "",
              done && wasSkipped ? "skipped" : "",
              i === stepIndex ? "current" : "",
              reachable ? "clickable" : "",
            ]
              .filter(Boolean)
              .join(" ");
            return (
              <button
                key={s.key}
                type="button"
                className={cls}
                disabled={!reachable}
                onClick={() => reachable && goToStep(i)}
              >
                <span className="n">
                  {done ? <Icon name={wasSkipped ? "clock" : "check"} size={13} /> : i + 1}
                </span>
                <span>
                  <span className="t">{s.title}</span>
                  <span className="s">
                    {done && wasSkipped ? "Skipped — finish later" : s.sub}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="ad-sec">
          <div className="ad-sec-head">
            <div>
              <h2>{step.title}</h2>
              <p>{step.desc}</p>
            </div>
            {stepIndex > 0 && step.key !== "sync" && stepIndex < STEPS.length - 1 && (
              <Pill kind="info">Optional</Pill>
            )}
          </div>
          <div className="ad-sec-body">
            {error && (
              <div className="form-error" style={{ marginBottom: 14 }}>
                {error}
              </div>
            )}

            {step.key === "company" && (
              <CompanyStep
                selected={company}
                onSelect={pickCompany}
                name={name}
                setName={setName}
                webAddress={webAddress}
                setWebAddress={setWebAddress}
                phone={phone}
                setPhone={setPhone}
                address1={address1}
                setAddress1={setAddress1}
                address2={address2}
                setAddress2={setAddress2}
                city={city}
                setCity={setCity}
                stateVal={stateVal}
                setStateVal={setStateVal}
                postalCode={postalCode}
                setPostalCode={setPostalCode}
              />
            )}

            {step.key === "signin" && (
              <>
                <div className="ad-grid">
                  <Field
                    label="Microsoft 365 tenant ID"
                    hint="From Entra ID › Overview"
                    span
                    note="Paste the Directory (tenant) ID. It verifies the first time someone signs in."
                  >
                    <input
                      value={tenantId}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      onChange={(e) => setTenantId(e.target.value)}
                    />
                  </Field>
                  <Field label="Trust mode">
                    <select
                      value={trustMode}
                      onChange={(e) => setTrustMode(e.target.value as SsoTrustMode)}
                    >
                      <option value="b2b_guest">B2B guest (recommended)</option>
                      <option value="federated">Federated (direct multi-tenant)</option>
                    </select>
                  </Field>
                  <Field label="Allowed email domains" hint="Only these domains can sign in">
                    <ChipInput
                      values={domains}
                      onAdd={(v) => setDomains((d) => [...d, v])}
                      onRemove={(v) => setDomains((d) => d.filter((x) => x !== v))}
                      placeholder="Add a domain and press Enter"
                    />
                  </Field>
                </div>
                <div style={{ marginTop: 14 }}>
                  <ToggleRow
                    label="Create accounts on first sign-in"
                    sub="Anyone from an allowed domain gets a portal user automatically."
                    checked={autoProvision}
                    onChange={setAutoProvision}
                  />
                  <ToggleRow
                    label="Require MFA"
                    sub="Enforced through Entra Conditional Access."
                    checked={requireMfa}
                    onChange={setRequireMfa}
                  />
                </div>
              </>
            )}

            {step.key === "people" && (
              <>
                {contacts.length > 0 ? (
                  <>
                    <div className="ad-eyebrow" style={{ marginBottom: 10 }}>
                      Contacts in Autotask
                    </div>
                    {contacts.map((c) => {
                      const isAdmin = adminContactId === c.id;
                      const ticked = selectedContacts.includes(c.id);
                      return (
                        <label key={c.id} className={`ad-pick${isAdmin ? " sel" : ""}`}>
                          <input
                            type="checkbox"
                            checked={ticked}
                            onChange={(e) => {
                              setSelectedContacts((prev) =>
                                e.target.checked
                                  ? [...prev, c.id]
                                  : prev.filter((x) => x !== c.id),
                              );
                              if (!e.target.checked && isAdmin) setAdminContactId(null);
                            }}
                          />
                          <AdAvatar name={c.full_name || (c.email ?? "?")} />
                          <span className="bd">
                            <span className="t">
                              {c.full_name || nameFromEmail(c.email as string)}
                              {isAdmin && (
                                <span style={{ marginLeft: 6 }}>
                                  <Pill kind="info">Client admin</Pill>
                                </span>
                              )}
                            </span>
                            <span className="s">{c.email}</span>
                          </span>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={(e) => {
                              e.preventDefault();
                              setAdminContactId(c.id);
                              setSelectedContacts((prev) =>
                                prev.includes(c.id) ? prev : [...prev, c.id],
                              );
                            }}
                          >
                            {isAdmin ? "Admin" : "Make admin"}
                          </button>
                        </label>
                      );
                    })}
                  </>
                ) : (
                  <Callout>
                    No contacts with an email address are synced from Autotask for this
                    company yet. Add the first admin by email below.
                  </Callout>
                )}
                <div className="ad-grid" style={{ marginTop: 18 }}>
                  <Field label="Or invite an admin by email" span>
                    <input
                      type="email"
                      value={adminEmail}
                      placeholder={`name@${domains[0] ?? "company.com.au"}`}
                      onChange={(e) => setAdminEmail(e.target.value)}
                    />
                  </Field>
                </div>
              </>
            )}

            {step.key === "forms" && (
              <>
                <div style={{ marginBottom: 14 }}>
                  <Callout kind="ok">
                    Every published global form is on by default. Untick anything that
                    doesn&apos;t apply to this customer.
                  </Callout>
                </div>
                {globalForms.length === 0 ? (
                  <div className="ad-note">
                    No published global forms in the catalogue yet.
                  </div>
                ) : (
                  globalForms.map((f) => {
                    const on = !disabledForms.includes(f.id);
                    return (
                      <label key={f.id} className={`ad-access${on ? "" : " off"}`}>
                        <span className="ico">
                          <Icon name="doc" />
                        </span>
                        <span className="bd">
                          <span className="t">{f.client_facing_title || f.title}</span>
                          <span className="s">{f.category ?? "Uncategorised"}</span>
                        </span>
                        <span className="ad-toggle" style={{ padding: 0, border: 0 }}>
                          <input
                            type="checkbox"
                            checked={on}
                            aria-label={`Enable ${f.title}`}
                            onChange={(e) =>
                              setDisabledForms((prev) =>
                                e.target.checked
                                  ? prev.filter((x) => x !== f.id)
                                  : [...prev, f.id],
                              )
                            }
                          />
                          <span className="ad-track">
                            <span className="ad-knob" />
                          </span>
                        </span>
                      </label>
                    );
                  })
                )}
                <div className="ad-grid" style={{ marginTop: 20 }}>
                  <Field
                    label="Default approver for approval forms"
                    span
                    note="One routing rule is created from this. Add exceptions (spend limits, categories) later under Forms & approvals."
                  >
                    <select
                      value={approver}
                      onChange={(e) => setApprover(e.target.value as ApproverChoice)}
                    >
                      {APPROVER_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
              </>
            )}

            {step.key === "sync" && (
              <>
                <div style={{ marginBottom: 14 }}>
                  <Callout kind="ok">
                    Everything here is on by default. Switch off anything this
                    customer shouldn&apos;t have — you can change all of it later
                    under Ticket sync.
                  </Callout>
                </div>
                <ToggleRow
                  label="Show Autotask tickets"
                  sub={
                    company
                      ? `Tickets for company AT ${company.autotask_id} appear under Tickets.`
                      : "Their Autotask tickets appear under Tickets."
                  }
                  checked={readEnabled}
                  onChange={setReadEnabled}
                />
                <ToggleRow
                  label="Create tickets in Autotask from the portal"
                  sub={
                    writeGloballyOff ? (
                      <>
                        Held off globally while the Autotask credentials are
                        read-only. <Pill kind="warn">Global flag off</Pill>
                      </>
                    ) : (
                      "Portal-raised tickets are written straight into Autotask. The same switch covers reopen, resolve, and close, and a customer note or attachment moves the ticket to Customer Note Added."
                    )
                  }
                  checked={writeEnabled}
                  onChange={setWriteEnabled}
                />
                <ToggleRow
                  label="Show technician notes"
                  sub="Non-internal Autotask notes appear in the ticket thread."
                  checked={syncNotes}
                  onChange={setSyncNotes}
                />
                <ToggleRow
                  label="Let users reply on tickets"
                  sub="Portal users can add notes and attachments to their own tickets."
                  checked={clientCanAddNotes}
                  onChange={setClientCanAddNotes}
                />
                <div className="ad-grid" style={{ marginTop: 20 }}>
                  <Field
                    label="Default queue for new tickets"
                    note="Where portal-raised tickets land in Autotask."
                  >
                    <select
                      value={effectiveQueueId}
                      onChange={(e) => setQueueId(e.target.value)}
                    >
                      <option value="">— choose —</option>
                      {(syncOptions?.available_queues ?? []).map((q) => (
                        <option key={q.value} value={String(q.value)}>
                          {q.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Default priority">
                    <select
                      value={effectivePriorityId}
                      onChange={(e) => setPriorityId(e.target.value)}
                    >
                      <option value="">— choose —</option>
                      {(syncOptions?.available_priorities ?? []).map((pr) => (
                        <option key={pr.value} value={String(pr.value)}>
                          {pr.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                <div className="ad-note" style={{ marginTop: 12 }}>
                  Status display overrides (renaming an Autotask status for portal
                  users) are set per customer once they exist.
                </div>
              </>
            )}

            {step.key === "review" && (
              <div className="ad-review">
                <ReviewBlock title="Company" onEdit={() => goToStep(0)}>
                  <KeyValue k="Name" v={name || "—"} />
                  <KeyValue
                    k="Autotask"
                    v={company ? `AT ${company.autotask_id}` : "—"}
                  />
                  <KeyValue k="Phone" v={phone || "—"} />
                  <KeyValue k="Web" v={webAddress || "—"} />
                </ReviewBlock>
                <ReviewBlock
                  title="Sign-in"
                  onEdit={() => goToStep(1)}
                  skipMessage={
                    tenantId.trim()
                      ? null
                      : "Skipped — users can't sign in until a tenant is connected."
                  }
                >
                  <KeyValue k="Tenant" v={`${tenantId.slice(0, 8)}…`} />
                  <KeyValue k="Domains" v={domains.join(", ") || "—"} />
                  <KeyValue k="Auto-provision" v={autoProvision ? "On" : "Off"} />
                  <KeyValue k="MFA" v={requireMfa ? "Required" : "Off"} />
                </ReviewBlock>
                <ReviewBlock
                  title="People"
                  onEdit={() => goToStep(2)}
                  skipMessage={
                    adminContactId !== null || adminEmail.trim()
                      ? null
                      : "Skipped — nobody at the customer can manage users yet."
                  }
                >
                  <KeyValue
                    k="Client admin"
                    v={
                      adminContactId !== null
                        ? contacts.find((c) => c.id === adminContactId)?.full_name ||
                          contacts.find((c) => c.id === adminContactId)?.email ||
                          "—"
                        : adminEmail.trim() || "—"
                    }
                  />
                  <KeyValue
                    k="Also invited"
                    v={`${
                      selectedContacts.filter((id) => id !== adminContactId).length
                    } contact(s)`}
                  />
                </ReviewBlock>
                <ReviewBlock title="Forms & approvals" onEdit={() => goToStep(3)}>
                  <KeyValue
                    k="Forms enabled"
                    v={`${globalForms.length - disabledForms.length} of ${globalForms.length}`}
                  />
                  <KeyValue
                    k="Approver"
                    v={APPROVER_OPTIONS.find((o) => o.value === approver)?.label ?? "—"}
                  />
                </ReviewBlock>
                <ReviewBlock
                  title="Ticket sync"
                  onEdit={() => goToStep(4)}
                  skipMessage={
                    readEnabled
                      ? null
                      : "Tickets are hidden — this customer sees an empty Tickets page."
                  }
                >
                  <KeyValue k="Show tickets" v={readEnabled ? "On" : "Off"} />
                  <KeyValue
                    k="Create in Autotask"
                    v={
                      !writeEnabled
                        ? "Off"
                        : writeGloballyOff
                          ? "On — held by the global flag"
                          : "On"
                    }
                  />
                  <KeyValue k="Technician notes" v={syncNotes ? "Shown" : "Hidden"} />
                  <KeyValue k="Replies" v={clientCanAddNotes ? "Allowed" : "Off"} />
                  <KeyValue
                    k="Status changes"
                    v={
                      !writeEnabled
                        ? "Off (follows create)"
                        : writeGloballyOff
                          ? "Held by the global flag"
                          : "On (follows create)"
                    }
                  />
                  <KeyValue
                    k="Queue"
                    v={
                      (syncOptions?.available_queues ?? []).find(
                        (q) => String(q.value) === effectiveQueueId,
                      )?.label ?? "—"
                    }
                  />
                  <KeyValue
                    k="Priority"
                    v={
                      (syncOptions?.available_priorities ?? []).find(
                        (pr) => String(pr.value) === effectivePriorityId,
                      )?.label ?? "—"
                    }
                  />
                </ReviewBlock>
              </div>
            )}

            <div className="ad-wiz-foot">
              <span>
                {stepIndex > 0 && (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => goToStep(stepIndex - 1)}
                    disabled={busy}
                  >
                    <Icon name="arrowLeft" size={15} /> Back
                  </button>
                )}
              </span>
              <span className="grp">
                {stepIndex > 0 && step.key !== "sync" && stepIndex < STEPS.length - 1 && (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => advance(true)}
                    disabled={busy}
                  >
                    Skip for now
                  </button>
                )}
                {step.key === "review" ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => void handleCreate()}
                    disabled={busy || !company}
                  >
                    {busy ? "Creating…" : "Create customer"}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => advance(false)}
                    disabled={!canContinue}
                  >
                    Continue <Icon name="arrowRight" size={15} />
                  </button>
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReviewBlock({
  title,
  onEdit,
  skipMessage,
  children,
}: {
  title: string;
  onEdit: () => void;
  skipMessage?: string | null;
  children: React.ReactNode;
}) {
  return (
    <div className="blk">
      <h4>
        {title}
        <button type="button" onClick={onEdit}>
          Edit
        </button>
      </h4>
      {skipMessage ? (
        <div className="ad-callout warn" style={{ padding: "8px 10px", fontSize: 12.5 }}>
          <Icon name="info" />
          <span>{skipMessage}</span>
        </div>
      ) : (
        children
      )}
    </div>
  );
}

/** Step 1: search the synced Autotask company mirror, then edit the prefill. */
function CompanyStep({
  selected,
  onSelect,
  name,
  setName,
  webAddress,
  setWebAddress,
  phone,
  setPhone,
  address1,
  setAddress1,
  address2,
  setAddress2,
  city,
  setCity,
  stateVal,
  setStateVal,
  postalCode,
  setPostalCode,
}: {
  selected: AutotaskCompanyDetail | null;
  onSelect: (d: AutotaskCompanyDetail) => void;
  name: string;
  setName: (v: string) => void;
  webAddress: string;
  setWebAddress: (v: string) => void;
  phone: string;
  setPhone: (v: string) => void;
  address1: string;
  setAddress1: (v: string) => void;
  address2: string;
  setAddress2: (v: string) => void;
  city: string;
  setCity: (v: string) => void;
  stateVal: string;
  setStateVal: (v: string) => void;
  postalCode: string;
  setPostalCode: (v: string) => void;
}) {
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const id = window.setTimeout(() => setQuery(input.trim()), 300);
    return () => window.clearTimeout(id);
  }, [input]);

  const { data: results, isLoading } = useSWR<AutotaskCompanySearchItem[]>(
    query.length >= 2 ? ["/api/admin/autotask/companies", query] : null,
    () => api.admin.autotask.searchCompanies(query),
    { keepPreviousData: true },
  );

  async function choose(autotaskId: number) {
    setLoadingId(autotaskId);
    setError(null);
    try {
      onSelect(await api.admin.autotask.companyDetails(autotaskId));
    } catch (e) {
      setError(errText(e, "Could not load that Autotask company."));
    } finally {
      setLoadingId(null);
    }
  }

  return (
    <>
      <div className="rf-search" style={{ marginBottom: 14, maxWidth: "none" }}>
        <Icon name="search" />
        <input
          type="search"
          value={input}
          placeholder="Search Autotask companies…"
          aria-label="Search Autotask companies"
          onChange={(e) => setInput(e.target.value)}
        />
      </div>

      {error && (
        <div className="form-error" style={{ marginBottom: 12 }}>
          {error}
        </div>
      )}

      {query.length < 2 ? (
        <div className="ad-note">
          Type at least 2 characters. Results come from the last Autotask directory sync.
        </div>
      ) : isLoading && !results ? (
        <div className="ad-note">Searching…</div>
      ) : (results ?? []).length === 0 ? (
        <div className="ad-note">
          No synced Autotask company matches “{query}”. The list is a snapshot from the
          last sync.
        </div>
      ) : (
        (results ?? []).map((r) => (
          <button
            key={r.autotask_id}
            type="button"
            className={`ad-pick${selected?.autotask_id === r.autotask_id ? " sel" : ""}`}
            disabled={loadingId !== null}
            onClick={() => void choose(r.autotask_id)}
          >
            <AdAvatar name={r.name} />
            <span className="bd">
              <span className="t">{r.name}</span>
              <span className="s">{r.is_active ? "Active in Autotask" : "Inactive"}</span>
            </span>
            <span className="mono">
              {loadingId === r.autotask_id ? "Loading…" : `AT ${r.autotask_id}`}
            </span>
          </button>
        ))
      )}

      {selected?.already_linked && (
        <div style={{ marginTop: 14 }}>
          <Callout kind="warn">
            <strong>{selected.already_linked.name}</strong> already links this Autotask
            company.{" "}
            <Link className="ad-link" to={`/admin/clients/${selected.already_linked.id}`}>
              Open it instead
            </Link>
            .
          </Callout>
        </div>
      )}

      {selected && (
        <div className="ad-grid" style={{ marginTop: 20 }}>
          <Field label="Portal name">
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
          </Field>
          <Field label="Website">
            <input
              value={webAddress}
              onChange={(e) => setWebAddress(e.target.value)}
              maxLength={255}
            />
          </Field>
          <Field label="Phone">
            <input value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} />
          </Field>
          <Field label="City">
            <input value={city} onChange={(e) => setCity(e.target.value)} maxLength={100} />
          </Field>
          <Field label="Address line 1" span>
            <input
              value={address1}
              onChange={(e) => setAddress1(e.target.value)}
              maxLength={255}
            />
          </Field>
          <Field label="Address line 2" span>
            <input
              value={address2}
              onChange={(e) => setAddress2(e.target.value)}
              maxLength={255}
            />
          </Field>
          <Field label="State">
            <input
              value={stateVal}
              onChange={(e) => setStateVal(e.target.value)}
              maxLength={50}
            />
          </Field>
          <Field label="Postcode">
            <input
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              maxLength={20}
            />
          </Field>
        </div>
      )}
    </>
  );
}
